import { LadderWatcher } from './components/Ladder'
import { dayKey } from './lib/datetime';
import { dayNumber } from './lib/dailyGame';
import { Routes, Route, useLocation, useNavigate, useParams, Navigate } from 'react-router-dom'
import { logEvent } from './lib/analytics'
import { ErrorBoundary } from './components/ErrorBoundary'
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react'
import { useFollows }      from './hooks/useFollows'
import { prefetchCoWatch } from './hooks/useCoWatch'
import { useAuth } from './hooks/useAuth'
import { useWatchlist }    from './hooks/useWatchlist'
import { useActiveRaces } from './hooks/useActiveRaces'
import { supabase } from './lib/supabase'
import { loadTheme, applyTheme, saveTheme, MODES } from './lib/theme'
import { setNowPlayingBase } from './lib/nowPlaying'
import { BottomNav } from './components/BottomNav'
import { GuestNav } from './components/GuestNav'
import { InviteNudge } from './components/InviteNudge'
import { PushAfterFirstMark } from './components/PushAfterFirstMark'
import { SaveListAsk } from './components/SaveListAsk'
import { InstallAsk } from './components/InstallAsk'
import { shareInvite } from './lib/inviteShare'
import { readPendingRoom, clearPendingRoom } from './lib/pendingRoom'
import { safeNext } from './lib/safeNext'
const CuratorListsPage = lazy(() => import('./pages/CuratorLists'))
const RollCreditsPage  = lazy(() => import('./pages/RollCredits'))
import { ToastProvider } from './components/Toast'
import { PlansProvider, CheckInController, usePlans } from './components/TonightMode'
// Lazy: keeps ShowStatusSheet out of the entry chunk (it rode CinematicLayer's
// lazy boundary before moving here).
const ShowAddedSheet = lazy(() => import('./components/ShowStatusSheet').then(m => ({ default: m.ShowAddedSheet })))
import { DesktopShell } from './components/DesktopShell'
import { OfflineBanner } from './components/OfflineBanner'
import { useMyServices } from './lib/streaming.jsx'
import { useFeatureFlags } from './lib/featureFlags'
import { FeatureGate } from './components/FeatureGate'
import { OpsAlertBanner } from './components/OpsAlertBanner'
import { AnnouncementBanner } from './components/AnnouncementBanner'
import { AuthModal } from './components/AuthModal'
import { guestAddShow } from './lib/guestAdd'
import { reportSaveError } from './lib/saveError'
import { TitleCard, cardForPath } from './components/TitleCard'
import { GameOpen, GAMES } from './components/games'

import GuestHome      from './pages/GuestHome'  // eager: the logged-OUT '/' landing (marketing)
// The authenticated Home is now lazy like every other route: logged-out visitors render
// GuestHome and never download the heavy authenticated Home tree (~114 kB gzip) on the
// coldest, most conversion-critical load (2026-07-06 perf audit).
// The rest are code-split — not on the first-paint path, covered by the <Suspense> that
// wraps <Routes>. (CalendarPage + TonightPlansPage were dead imports here — TonightHub
// pulls them itself — so they're gone entirely.)
const SearchPage   = lazy(() => import('./pages/Search'))
// v17 Home: the Up Next deck (pages/HomeDeck.jsx). The 16.x Home stays in the tree until the cut batch.
const HomeDeck     = lazy(() => import('./pages/HomeDeck'))
const ShowPage17   = lazy(() => import('./pages/ShowPage17'))
const MyList17     = lazy(() => import('./pages/MyList17'))
const Tonight17    = lazy(() => import('./pages/Tonight17'))
const Discover17   = lazy(() => import('./pages/Discover17'))
const Stats17      = lazy(() => import('./pages/Stats17'))
const History17    = lazy(() => import('./pages/History17'))
const Notifications17 = lazy(() => import('./pages/Notifications17'))
const Profile17    = lazy(() => import('./pages/Profile17'))
const ForYouPage   = lazy(() => import('./pages/ForYou'))
const LoginPage    = lazy(() => import('./pages/Login'))
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword'))
const AuthConfirmPage   = lazy(() => import('./pages/AuthConfirm'))
// Code-split: heavy and/or rarely-first-paint pages load on demand (covered by the
// top-level Suspense boundary around <Routes>). The five bottom-nav tabs + Login
// stay eager so the initial navigation never shows a fallback.
const SettingsPage     = lazy(() => import('./pages/Settings'))
const AftershowPage    = lazy(() => import('./pages/Aftershow'))
const AftershowIndexPage = lazy(() => import('./pages/AftershowIndex'))
const SharePage        = lazy(() => import('./pages/Share'))
const AdminPage        = lazy(() => import('./pages/Admin'))
const AdminCanonsPage  = lazy(() => import('./pages/AdminCanons'))
const AdminOnlinePage  = lazy(() => import('./pages/AdminOnline'))
const ComponentLibraryPage = lazy(() => import('./pages/ComponentLibrary'))
const PeoplePage       = lazy(() => import('./pages/People'))
const BuzzPage         = lazy(() => import('./pages/Buzz'))
const ReadingRoomPage  = lazy(() => import('./pages/ReadingRoom'))
const GuestOnboardingPage = lazy(() => import('./pages/GuestOnboarding'))
const LiveBridge = lazy(() => import('./pages/LiveBridge'))
const PublicProfile    = lazy(() => import('./pages/PublicProfile'))
const WrappedPage      = lazy(() => import('./pages/Wrapped'))
const TimelinePage     = lazy(() => import('./pages/Timeline'))
const RewindPage       = lazy(() => import('./pages/Rewind'))
const ShowDNAPage      = lazy(() => import('./pages/ShowDNA'))
const HelpPage         = lazy(() => import('./pages/Help'))
const LegalPage        = lazy(() => import('./pages/Legal'))
const UnsubscribePage  = lazy(() => import('./pages/Unsubscribe'))
const Cliffhanger      = lazy(() => import('./pages/Cliffhanger'))
const FaceOff          = lazy(() => import('./pages/FaceOff'))
const Verdict          = lazy(() => import('./pages/Verdict'))
const ShelfDetail      = lazy(() => import('./pages/ShelfDetail'))
const SharePlansPage   = lazy(() => import('./pages/SharePlans'))
const PersonPage       = lazy(() => import('./pages/Person'))
const InvitePage       = lazy(() => import('./pages/Invite'))
const JoinRoomPage     = lazy(() => import('./pages/JoinRoom'))
const SwipeRoomPage    = lazy(() => import('./pages/SwipeRoom'))
const UniversesPage       = lazy(() => import('./pages/Universes'))
const UniverseDetailPage  = lazy(() => import('./pages/UniverseDetail'))
import { useRealTimeNotifs, ActivityToastStack } from './components/ActivityToast'
import { EdgeSwipeBack } from './components/EdgeSwipeBack'
// NotifLayer bundles the event/condition-driven toast+celebration rail behind
// one lazy boundary — CelebrationListener, AchievementUnlockListener,
// BirthdayCelebration, EpisodeBurstListener, RatingSavedToast, ReviewInvite,
// SaveErrorToast, UndoToast, AdminFab, WatchReturnPrompt, MarqueeEngine. See
// that file's header for the full rationale (mirrors CinematicLayer, 2026-08-15).
const NotifLayer = lazy(() => import('./components/NotifLayer'))
import { useActiveUsers } from './hooks/useActiveUsers'
const FinalCutShelf    = lazy(() => import('./pages/FinalCutShelf'))
const ScreenshotImport = lazy(() => import('./pages/ScreenshotImport'))

// ── Off the entry chunk (perf, 2026-08-01) ───────────────────────────────────
// One-shot cinematics/modals behind lazy boundaries. CinematicLayer bundles the
// event-driven set (see its header for why the race is safe); the three below are
// condition-gated at their render sites, so their chunks load only when the
// condition first turns true. Together these were ~40% of the eager entry chunk.
const CinematicLayer   = lazy(() => import('./components/CinematicLayer'))
const WelcomeWalkthrough = lazy(() => import('./components/Welcome').then(m => ({ default: m.WelcomeWalkthrough })))
const WhatsNew         = lazy(() => import('./components/WhatsNew').then(m => ({ default: m.WhatsNew })))
const PasskeyNudge     = lazy(() => import('./components/PasskeyNudge').then(m => ({ default: m.PasskeyNudge })))
const EpisodePulseHost = lazy(() => import('./components/EpisodePulse').then(m => ({ default: m.EpisodePulseHost })))
const ImportStart      = lazy(() => import('./pages/ImportStart'))
const SyncList         = lazy(() => import('./pages/SyncList'))
import { safeStorage } from './lib/safeStorage'
import { liveSince } from './lib/liveSince'
import { resolveCoWatchMark } from './lib/cowatch'
import { APP_VERSION } from './lib/appVersion'

// Was a bare amber border-ring; swapped for a spinning film-reel disc (Jason's Claude
// Design export, 2026-08-06) — reads as NextEp on sight the same way the popcorn splash
// does, instead of a generic loading ring any app could have.
// The dailies' chunks load behind their own title card (GameOpen), so a cold open
// is one continuous beat from the tap to the board instead of reel → card (2026-10-18).
const LazyFallback = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const game = GAMES.find(g => path === g.route)
  if (game) return <GameOpen game={game.key} />
  const c = cardForPath(path)
  return <TitleCard title={c.title} line={c.line} caption={c.caption} />
}


const NO_NAV = ['/login', '/start', '/swipe', '/reset-password']

// Legacy /universes/:id → /canon/:id. `Navigate`'s `to` can't read route params
// directly, so this tiny wrapper reads the id itself before redirecting —
// otherwise every legacy deep link lands on the bare hub instead of the show.

// Inner component: routes + the per-user effects (badge engine retired 2026-10-12)
// Thin bridge so DesktopShell can read today's plan count without AppRoutes
function TonightCountBridge({ onCount }) {
  const { todayPending } = usePlans() || {}
  const n = todayPending?.length || 0
  useEffect(() => { onCount(n) }, [n, onCount])
  return null
}

// itself needing to call usePlans() — PlansProvider is a CHILD of AppRoutes'
// own render, so only a component rendered inside it can consume the context.
function DesktopShellSlot({ pathname, profile, cliffUnplayed, faceoffUnplayed, verdictUnplayed, featureFlags }) {
  const { todayPending } = usePlans() || {}
  return <DesktopShell pathname={pathname} profile={profile} cliffUnplayed={cliffUnplayed} faceoffUnplayed={faceoffUnplayed} verdictUnplayed={verdictUnplayed} tonightCount={todayPending?.length} featureFlags={featureFlags} />
}

function AppRoutes({ user, profile, wl, booted, signOut, refreshProfile, showAuthModal, setShowAuthModal, myServices, featureFlags, unseenActivity, setUnseenActivity, unseenNotifs, onClearNotifs, bingeShow, setBingeShow, onClearUnseen, raceShowIds, onlineUsers, newFollowers, cliffUnplayed, faceoffUnplayed, verdictUnplayed, liveUpdates, onOpenLive, onClearFollowers }) {
  const { pathname }        = useLocation()
  const navigate              = useNavigate()
  // Tonight's pending-plan count for the tab badge. BottomNav renders OUTSIDE
  // PlansProvider, so a tiny bridge inside the provider lifts the number out.
  const [tonightCount, setTonightCount] = useState(0)

  // Co-watch "mark yours too" — one-tap sync. When a pact-mate watches an episode,
  // ActivityToast surfaces a nudge; tapping it fires this instead of navigating, so
  // the shared episode is marked here (App owns wl + the show's season data).
  // 17.087: 17.086's badge-engine pruning took this listener out with it, leaving the
  // toast's tap a dead end for a day. Restored verbatim; the sweep now guards it.
  useEffect(() => {
    const onCoWatchMark = (e) => {
      const { show_id, season, episode, show_name } = e.detail || {}
      if (!show_id) return
      const show = wl.items?.find(s => s.id === show_id || s.id === +show_id)
      const watched = { season: Number(season), episode: Number(episode) }
      const res = resolveCoWatchMark({ show, current: wl.progress?.[show_id], watched })
      if (res.action === 'navigate' || !wl.saveProgress) { navigate('/show/' + show_id); return }
      if (res.action === 'synced') {
        window.dispatchEvent(new CustomEvent('nextep:toast', { detail: {
          title: '🍿 Already in sync', accent: 'var(--green)',
          body: `You're caught up with ${show_name || 'them'} on this one.`,
        } }))
        return
      }
      wl.saveProgress(show_id, res.next, { watchedEp: watched })
      window.dispatchEvent(new CustomEvent('nextep:toast', { detail: {
        title: '🍿 Marked, back in sync', accent: 'var(--amber)',
        body: `Marked S${watched.season}·E${watched.episode} of ${show_name || 'your show'} watched. You're together again.`,
      } }))
    }
    window.addEventListener('nextep:cowatch-mark', onCoWatchMark)
    return () => window.removeEventListener('nextep:cowatch-mark', onCoWatchMark)
  }, [wl, navigate])

  // Desktop: "/" opens search from anywhere (not while typing in a field).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      e.preventDefault(); navigate('/search')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])


  // Page views — the one funnel signal app_events never had. A churned account's trail
  // read "landing_view → authed_boot → (nothing)", which can't distinguish "browsed 20
  // shows and left" from "bounced off Home". Route FAMILY only (/show/123 → /show): no
  // ids, no query strings. (Jason 2026-09-08: wire activity for signups who stop.)
  const lastPageRef = useRef(null)
  useEffect(() => {
    const fam = '/' + (pathname.split('/')[1] || 'home')
    if (lastPageRef.current === fam) return
    logEvent('page_view', { path: fam, from: lastPageRef.current || 'boot' })
    lastPageRef.current = fam
  }, [pathname])

  // Post-auth resume into a shared Viewing Room. A "/join/:roomId" recipient who
  // wasn't signed in went join → login and lands back here on "/" (magic link) or
  // any route (same-browser). Once a session exists, hop them into the room via the
  // same stash ShowDetail's join effect consumes. Fires once per stashed room; the
  // /join route itself handles the already-signed-in case, so this only catches the
  // returning-from-login path. (2026-07-24, co-watch viral loop.)
  const joinedRoomRef = useRef(false)
  useEffect(() => {
    if (!user?.id || joinedRoomRef.current) return
    const pending = readPendingRoom()
    if (!pending?.roomId) return
    joinedRoomRef.current = true
    try { sessionStorage.setItem('nextep_join_room', String(pending.roomId)) } catch { /* storage blocked */ }
    clearPendingRoom()
    navigate(pending.showId ? `/show/${pending.showId}` : '/', { replace: true })
  }, [user?.id, navigate])

  // Post-auth resume to wherever you were when you hit a sign-in wall. The password
  // path sets location.href directly, but a MAGIC LINK leaves the browser and returns to
  // "/" with ?next= riding in the query string (see withInvite in Login), so the hop has
  // to happen here once a session exists. Mirrors the join-room resolver above.
  //
  // safeNext, not the raw param: an unvalidated redirect target is an open redirect, and
  // "//evil.com" is accepted by both navigate and location.href. Same-origin PATHS only.
  // Runs after the join-room effect so a room link still wins — that hop is time-critical
  // (a 1-hour TTL) and a ?next= is only ever a convenience.
  const nextHopRef = useRef(false)
  useEffect(() => {
    if (!user?.id || nextHopRef.current || joinedRoomRef.current) return
    let raw = null
    try { raw = new URLSearchParams(window.location.search).get('next') } catch { /* no window */ }
    if (!raw) return
    const dest = safeNext(raw)
    nextHopRef.current = true
    // A rejected or no-op target still leaves ?next= sitting in the address bar, which
    // looks broken and, for a refused "//evil.com", looks alarming. Replace to the clean
    // path either way so the param never lingers.
    if (dest === '/' || dest === window.location.pathname) {
      navigate(window.location.pathname, { replace: true })
      return
    }
    navigate(dest, { replace: true })
  }, [user?.id, navigate])

  // Rate-from-card-reveal: the completion CardReveal now folds the rating in
  // (audit 2026-07-08), dispatching nextep:card-rate so it saves from any entry
  // point. Quiet (bulk) so a 5-reel doesn't stack the "5-Reel Honor" modal on top
  // of the completion moment.
  useEffect(() => {
    const onCardRate = (e) => {
      const { show_id, rating } = e.detail || {}
      if (show_id && rating) wl.rate?.(Number(show_id), rating, { bulk: true })
    }
    window.addEventListener('nextep:card-rate', onCardRate)
    return () => window.removeEventListener('nextep:card-rate', onCardRate)
  }, [wl])

  // Check for pending Watch Together (written by Invite.jsx in the Safari context
  // BEFORE this PWA booted). Reuse the profile useAuth already loaded — get_my_profile
  // returns the full own row incl. pending_watch_together — instead of firing a SECOND
  // identical definer RPC at boot. Ref-guarded so it handles a pending exactly once per
  // open, even if `profile` identity changes later (e.g. a settings refresh).
  // Track WHICH user was handled (not just a boolean) so a same-device account switch
  // (Alice → Bob, no reload) still processes Bob's pending — a plain boolean stayed true
  // and skipped every subsequent user.
  const pendingWTHandledFor = useRef(null)
  useEffect(() => {
    if (!user?.id || pendingWTHandledFor.current === user.id) return
    const pending = profile?.pending_watch_together
    if (!pending?.showId) return
    pendingWTHandledFor.current = user.id
    // Always clear the slot — it's one-shot either way.
    supabase.from('profiles').update({ pending_watch_together: null }).eq('id', user.id)
    // Age it out rather than yanking someone into a long-dead room. Older invites still
    // reach them passively via the bell notice, which now offers instead of auto-joining
    // (audit M4). Invites written before sentAt existed are treated as fresh once, then
    // cleared by the update above.
    const PENDING_MAX_AGE_MS = 6 * 60 * 60 * 1000
    if (pending.sentAt && Date.now() - new Date(pending.sentAt).getTime() > PENDING_MAX_AGE_MS) return
    try { sessionStorage.setItem('nextep_watch_together', JSON.stringify(pending)) } catch {}
    navigate(`/show/${pending.showId}`)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile])

  const showNav = !!user && !NO_NAV.some(p => pathname.startsWith(p))
  // Guests can already reach every browse page but had no way to move between them.
  // Give them a persistent nav (Discover/Search + a Sign-up CTA) everywhere EXCEPT the
  // landing page itself (which is the sign-up surface) and /login (Jason 2026-07-23).
  const showGuestNav = !user && pathname !== '/' && !NO_NAV.some(p => pathname.startsWith(p))

  return (
    <>
      {/* MilestoneModal removed: its only trigger (nextep:milestone-completion) was
          retired into the Marquee system, so it was a dead mount (Jason 2026-06-29). */}
      {booted && <ErrorBoundary label="cinematics" fallback={null}>
        <Suspense fallback={null}><CinematicLayer user={user} wl={wl} /></Suspense>
      </ErrorBoundary>}
      <OpsAlertBanner profile={profile} />
      {/* In normal document flow (not fixed) — pushes page content down rather
          than covering it, since this is meant to be visible to every user for
          days, not a rare admin-only overlay like OpsAlertBanner above. */}
      <AnnouncementBanner />
      <PlansProvider user={user} promoteFromPlan={wl.promoteFromPlan}>
      <main key={pathname} className="page-fade">
      <ErrorBoundary label="the page">
      <Suspense fallback={<LazyFallback/>}>
      <Routes>
        <Route path="/"         element={user ? <HomeDeck user={user} wl={wl} featureFlags={featureFlags} profile={profile} onlineUsers={onlineUsers} games={{ cliffhanger: cliffUnplayed, faceoff: faceoffUnplayed, verdict: verdictUnplayed }} /> : <GuestHome />} />
        <Route path="/discover" element={<Suspense fallback={<LazyFallback/>}><Discover17 wl={wl} myServices={myServices} user={user} /></Suspense>} />
        {/* Standalone route — manages its own Supabase session, does NOT read `user`
            (which resolves anonymous sessions to null on purpose, see useAuth.jsx).
            Entry point for GuestHome's "Browse without an account". */}
        <Route path="/start"    element={<GuestOnboardingPage />} />
        <Route path="/calendar" element={<Navigate to="/tonight" replace />} />
        <Route path="/list"     element={<Suspense fallback={<LazyFallback/>}><MyList17 wl={wl} /></Suspense>} />
        <Route path="/for-you" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/profile"  element={user ? <Suspense fallback={<LazyFallback/>}><Profile17 user={user} profile={profile} wl={wl} onSignOut={signOut} featureFlags={featureFlags} /></Suspense> : <Navigate to="/login?ref=profile" replace />} />
        <Route path="/settings" element={<Suspense fallback={<LazyFallback/>}><SettingsPage user={user} profile={profile} wl={wl} onRefreshProfile={refreshProfile} myServices={myServices} featureFlags={featureFlags} onSignOut={signOut} /></Suspense>} />
        {/* Same component, one level deep: /settings is the hub, /settings/:section
            is one section on its own screen. Deep-linkable on purpose — worth having
            when telling someone exactly where to tap. */}
        <Route path="/settings/:section" element={<Suspense fallback={<LazyFallback/>}><SettingsPage user={user} profile={profile} wl={wl} onRefreshProfile={refreshProfile} myServices={myServices} featureFlags={featureFlags} onSignOut={signOut} /></Suspense>} />
        <Route path="/show/:id" element={<Suspense fallback={<LazyFallback/>}><ShowPage17 wl={wl} user={user} featureFlags={featureFlags} /></Suspense>} />
        {/* A guest (anonymous session) must reach Login to save their list; only a real account bounces Home. */}
        <Route path="/login"    element={user && !user.is_anonymous ? <Navigate to="/" /> : <LoginPage />} />
        {/* Not gated on `user` — a recovery link plants its own temporary session
            regardless of any existing auth state, and updateUser({password}) needs
            that session live, so redirecting a signed-in user away here would break
            the flow for someone resetting from an already-logged-in device. */}
        <Route path="/reset-password" element={<Suspense fallback={<LazyFallback/>}><ResetPasswordPage /></Suspense>} />
        <Route path="/auth/confirm" element={<Suspense fallback={<LazyFallback/>}><AuthConfirmPage /></Suspense>} />
        <Route path="/challenges" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/share/:shareId" element={<SharePage wl={wl} />} />
        <Route path="/share/v2/:encoded" element={<SharePage wl={wl} />} />
        <Route path="/share/plans/:plans" element={<SharePlansPage wl={wl} />} />
        <Route path="/import" element={<Suspense fallback={null}><ImportStart wl={wl} user={user} profile={profile} /></Suspense>} />
        <Route path="/scan" element={<Suspense fallback={null}><ScreenshotImport wl={wl} user={user} profile={profile} /></Suspense>} />
        <Route path="/sync" element={<Suspense fallback={null}><SyncList wl={wl} user={user} /></Suspense>} />
        <Route path="/admin"        element={<AdminPage user={user} profile={profile} />} />
        <Route path="/admin/canons" element={<AdminCanonsPage profile={profile} />} />
        <Route path="/admin/online" element={<Suspense fallback={null}><AdminOnlinePage profile={profile} /></Suspense>} />
        <Route path="/ui"           element={<ComponentLibraryPage />} />
        <Route path="/people"       element={<FeatureGate featureKey="people" featureFlags={featureFlags}><PeoplePage user={user} wl={wl} onClearFollowers={onClearFollowers} /></FeatureGate>} />
        <Route path="/reviews" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/buzz" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/u/:username"  element={<PublicProfile currentUser={user} wl={wl} />} />
        <Route path="/stats"         element={<Suspense fallback={<LazyFallback/>}><Stats17 user={user} wl={wl} /></Suspense>} />
        <Route path="/timeline"      element={<Navigate to="/stats" replace />} />  {/* cut in v17 */}
        <Route path="/rewind"       element={<FeatureGate featureKey="wrapped" featureFlags={featureFlags}><RewindPage wl={wl} /></FeatureGate>} />
        <Route path="/dna"          element={<Navigate to="/stats" replace />} />  {/* cut in v17 */}
        {/* /vault retired 2026-07-28, same reasoning as /shelves: an archive with no
            clock looks identical every visit, so nobody returned to it. Its new-season
            signal moved to Tonight (lib/returningShows) and its rewatch scoring became
            the "Worth a rewatch" lens chip on My List — same math, no page to remember. */}
        <Route path="/vault"        element={<Navigate to="/list" replace />} />
        <Route path="/wrapped"        element={<FeatureGate featureKey="wrapped" featureFlags={featureFlags}><Suspense fallback={<LazyFallback/>}><WrappedPage user={user} wl={wl} profile={profile} /></Suspense></FeatureGate>} />
        <Route path="/help"          element={<HelpPage />} />
        <Route path="/privacy"       element={<Suspense fallback={<LazyFallback/>}><LegalPage doc="privacy" /></Suspense>} />
        <Route path="/terms"         element={<Suspense fallback={<LazyFallback/>}><LegalPage doc="terms" /></Suspense>} />
        <Route path="/delete-account" element={<Suspense fallback={<LazyFallback/>}><LegalPage doc="delete" /></Suspense>} />
        <Route path="/unsubscribe"   element={<Suspense fallback={<LazyFallback/>}><UnsubscribePage /></Suspense>} />
        <Route path="/notifications"    element={<Suspense fallback={<LazyFallback/>}><Notifications17 user={user} wl={wl} profile={profile} onClearUnseen={onClearUnseen} onClearNotifs={onClearNotifs} featureFlags={featureFlags} /></Suspense>} />
        <Route path="/cliffhanger" element={<FeatureGate featureKey="cliffhanger" featureFlags={featureFlags}><Cliffhanger /></FeatureGate>} />
        <Route path="/faceoff" element={<FeatureGate featureKey="faceoff" featureFlags={featureFlags}><FaceOff /></FeatureGate>} />
        <Route path="/verdict" element={<FeatureGate featureKey="verdict" featureFlags={featureFlags}><Verdict /></FeatureGate>} />
        <Route path="/person/:id"      element={<PersonPage wl={wl} />} />
        {/* /search is THE global search again (2026-09-14): your list + all shows + people
            + app pages from one input. Discover keeps its in-page box for filtered browsing. */}
        <Route path="/search"           element={<Suspense fallback={<LazyFallback/>}><SearchPage wl={wl} user={user} featureFlags={featureFlags} /></Suspense>} />
        <Route path="/invite/:code" element={<InvitePage />} />
        <Route path="/join/:roomId" element={<Suspense fallback={<LazyFallback/>}><JoinRoomPage /></Suspense>} />
        <Route path="/swipe/:roomId" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/cards" element={<Navigate to="/profile" replace />} />
        <Route path="/finalcut" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/tonight"      element={<Suspense fallback={<LazyFallback/>}><Tonight17 user={user} wl={wl} /></Suspense>} />
        <Route path="/history"      element={<Suspense fallback={<LazyFallback/>}><History17 user={user} /></Suspense>} />
        {/* /shelves retired 2026-07-28: smart shelves are now lens chips on My List,
            community lists are a row on Discover, and the page itself was a
            destination nobody returned to. Kept as a redirect so bookmarks, the PWA
            shortcut and any shared links still land somewhere sensible. */}
        <Route path="/shelves"        element={<Navigate to="/list" replace />} />
        {/* /lists also redirected to /list on 2026-07-28, which quietly orphaned
            Shelves (CuratorListsPage, née "Director's Chair") — the only real create-a-public-list
            flow in the app. My List's New Collection modal had grown a "make it
            public" toggle as a workaround; removing that (2026-08-02, private
            collections feature had no edit/delete and one user ever touched it)
            meant restoring the actual door instead of leaving none at all. */}
        <Route path="/lists" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/roll-credits" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/aftershow" element={<FeatureGate featureKey="aftershow" featureFlags={featureFlags}><Suspense fallback={<LazyFallback/>}><AftershowIndexPage wl={wl}/></Suspense></FeatureGate>} />
        <Route path="/aftershow/:showId" element={<FeatureGate featureKey="aftershow" featureFlags={featureFlags}><Suspense fallback={<LazyFallback/>}><AftershowPage wl={wl}/></Suspense></FeatureGate>} />
        <Route path="/curator/:id" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/canon" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/canon/:id" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/universes" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/universes/:id" element={<Navigate to="/" replace />} />  {/* cut in v17, see docs/v17-scope.md */}
        <Route path="/live"     element={<Suspense fallback={null}><LiveBridge /></Suspense>} />
        <Route path="*"         element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
      </ErrorBoundary>
      </main>
      <CheckInController user={user} wl={wl} />
      {/* The post-add sheet lives here, inside PlansProvider (moved out of
          CinematicLayer 2026-09-21), so its "put S1·E1 on Tonight?" beat can
          reach usePlans(). */}
      {booted && <Suspense fallback={null}><ShowAddedSheet wl={wl} /></Suspense>}
      <LadderWatcher user={user} profile={profile} onProfileChange={refreshProfile} wl={wl} />
      <TonightCountBridge onCount={setTonightCount} />
      {showNav && <DesktopShellSlot pathname={pathname} profile={profile} cliffUnplayed={cliffUnplayed} faceoffUnplayed={faceoffUnplayed} verdictUnplayed={verdictUnplayed} featureFlags={featureFlags} />}
      </PlansProvider>
      {user && <InviteNudge user={user} profile={profile} wl={wl} />}
      {user && <PushAfterFirstMark user={user} />}
      {user?.is_anonymous && <SaveListAsk user={user} shows={wl?.items?.length || 0} />}
      {user && <InstallAsk user={user} />}
      {showNav && <BottomNav wl={wl} user={user} tonightCount={tonightCount} unseenActivity={unseenActivity} unseenNotifs={unseenNotifs} onClearNotifs={onClearNotifs} newFollowers={newFollowers} cliffUnplayed={cliffUnplayed} faceoffUnplayed={faceoffUnplayed} verdictUnplayed={verdictUnplayed} liveUpdates={liveUpdates} onOpenLive={onOpenLive} featureFlags={featureFlags} onInvite={async () => {
        let res = 'failed'
        try { res = await shareInvite(profile, wl) } catch {}
        if (res === 'shared' || res === 'copied') window.dispatchEvent(new CustomEvent('nextep:toast', { detail: { title: res === 'copied' ? '🔗 Invite link copied' : '🎉 Thanks for sharing', accent: 'var(--green)', body: res === 'copied' ? 'Paste it to anyone you watch with.' : 'Every friend makes NextEp better.' } }))
        else if (res === 'failed') window.dispatchEvent(new CustomEvent('nextep:toast', { detail: { title: 'Couldn’t open the invite', accent: 'var(--amber)', body: 'Try again from More → Invite.' } }))
      }} />}
      {showGuestNav && <GuestNav />}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
      {/* Dev-only version footer (Jason, 2026-08-23) — import.meta.env.DEV is
          Vite's build-time flag, false in any production bundle (Netlify prod
          AND branch deploys both run `vite build`), so this never ships. Fixed
          + low-opacity + pointer-events:none so it never competes with the
          bottom nav for taps. */}
      {import.meta.env.DEV && (
        <div style={{
          position: 'fixed', top: 4, right: 6, zIndex: 'var(--z-max)',
          fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)', color: '#fff',
          background: 'rgba(0,0,0,0.75)', padding: '2px 6px', borderRadius: 5,
          pointerEvents: 'none', userSelect: 'none',
        }}>v{APP_VERSION}</div>
      )}
    </>
  )
}

// ── BootSplash ───────────────────────────────────────────────────────────────
// Shown once while useAuth resolves the session (its own 8s watchdog is the
// worst case, not the norm). Real usage data (friction telemetry, 2026-08-30)
// showed this screen getting rage-tapped repeatedly, over many separate
// sessions — a static, silent wait with nothing to look at reads as "is this
// stuck?" and invites tapping the wordmark like it might be a button. It
// isn't, and shouldn't pretend to be; the actual fix is reassurance, not a
// fake affordance. A line only appears after 2.5s specifically so a normal,
// fast boot never shows it — this is for the slow tail, not every load.
function BootSplash() {
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 2500)
    return () => clearTimeout(t)
  }, [])
  // Same classes as the pre-React cover in index.html (v17 rework 2026-10-15), so the
  // two phases are one continuous splash. The reassurance line still waits 2.5s.
  return (
    <div className="nx-splash" style={{ position: 'fixed' }}>
      <div className="nx-splash-glow a" /><div className="nx-splash-glow b" />
      <svg className="nx-splash-mark" viewBox="0 0 84 84" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="6" y="30" width="72" height="46" rx="7" fill="#15152a" stroke="#f5c842" strokeWidth="2.5"/>
        <rect x="6" y="30" width="72" height="12" fill="#f5c842"/>
        <path d="M18 30 l8 12 M34 30 l8 12 M50 30 l8 12 M66 30 l8 12" stroke="#07070f" strokeWidth="5"/>
        <g className="arm">
          <rect x="6" y="14" width="72" height="14" rx="4" fill="#f5c842"/>
          <path d="M20 14 l8 14 M36 14 l8 14 M52 14 l8 14 M68 14 l8 14" stroke="#07070f" strokeWidth="5"/>
        </g>
        <path d="M22 54 h32 M22 64 h20" stroke="rgba(245,200,66,.45)" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
      <div className="nx-splash-word"><span className="n">Next</span><span className="ep">EP</span></div>
      <div className="nx-splash-tag">Never miss an episode</div>
      <div className="nx-splash-film"><div className="beam" /></div>
      <div className="nx-splash-line" style={{ opacity: slow ? 1 : 0, transition: 'opacity 0.6s ease' }}>Finding your next episode…</div>
    </div>
  )
}

export default function App() {
  const { user, profile, loading, signOut, refreshProfile } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const [showWhatsNew, setShowWhatsNew] = useState(true)
  const myServices = useMyServices(user)
  const featureFlags = useFeatureFlags(user)

  // Sepia/Iridescent/Crystal/Gilt are gated skins: if one is the active theme but
  // this user isn't (or is no longer) entitled, revert to Dark. Runs once the
  // profile is known — keeps boot fast (initTheme applies localStorage first) and
  // cleanly handles revocation. Gilt has its OWN entitlement (ultra_skin) one gate
  // above the other three's premium_skin — losing premium_skin alone must not
  // silently downgrade a Gilt user who never had it revoked. is_admin bypasses
  // both checks entirely, matching Settings.jsx's picker unlock — an admin's
  // choice of Crystal/Iridescent/Gilt must never get silently reverted here.
  useEffect(() => {
    if (!profile || profile.is_admin) return
    const t = loadTheme()
    // Derived from MODES rather than a hand-listed set of skin names: the old list
    // (sepia/iridescent/crystal) silently missed Crimson when it shipped in 16.179,
    // so a de-entitled user could have kept a gated skin via localStorage.
    const cfg = MODES[t.mode]
    const lostPremium = !profile.premium_skin && !!cfg?.premium && !cfg?.ultra
    const lostUltra = !profile.ultra_skin && !!cfg?.ultra
    if (lostPremium || lostUltra) {
      applyTheme('dark', t.accent, t.hue)
      saveTheme('dark', t.accent, t.hue)
    }
  }, [profile])

  const welcomeShown = safeStorage.get('nextep_welcomed') === '1'
  const handleWelcomeDone = () => {
    safeStorage.set('nextep_welcomed','1'); setShowWelcome(false)
    // Persist onboarding completion server-side so it never re-shows on another
    // device (localStorage alone is per-device). welcome_seen_at is the durable flag.
    if (user?.id) supabase.from('profiles').update({ welcome_seen_at: new Date().toISOString() }).eq('id', user.id).then(() => {}, () => {})
  }

  // First-run: ONE surface — the WelcomeWalkthrough (it already welcomes by name and
  // ends on "add your first show"). The old flow stacked a confetti WelcomeOverlay in
  // front of it and gated the walkthrough on a fragile 2s timer + profile.username race
  // (some new users silently got no tour). This reacts to the profile actually LOADING
  // instead: wait for a non-null profile, then decide deterministically.
  useEffect(() => {
    if (!user || loading || welcomeShown) return
    if (!profile) return                       // profile still loading, wait (no timer)
    // A /start guest with a library (useAuth.asAppUser, 2026-09-12) already did an
    // onboarding — services, first shows, alerts — so the walkthrough's mode/services/
    // addshow steps would be a second onboarding on top of it. Their "welcome" is the
    // save-your-shows nudge on Home; the walkthrough waits for the real account.
    if (user.is_anonymous) return
    // Onboarded-ness is tracked by welcome_seen_at. This was mistakenly keyed off
    // `username`, but handle_new_user auto-assigns a username to EVERY signup — so the
    // check was always true and the walkthrough silently stopped firing for real new
    // users (and the ?from=tvtime import step with it). welcome_seen_at is null only
    // for a genuinely new account; set on complete/skip via handleWelcomeDone.
    if (profile.welcome_seen_at) {              // already onboarded → never re-show
      safeStorage.set('nextep_welcomed', '1')
      return
    }
    setShowWelcome(true)                         // genuinely new user: no welcome_seen_at yet
  }, [user?.id, profile, loading]) // eslint-disable-line

  const shareActivity    = profile?.notification_prefs?.share_activity || false
  const [unseenActivity,  setUnseenActivity]  = useState(false)
  const [unseenNotifs,    setUnseenNotifs]    = useState(0)
  const [bingeShow, setBingeShow] = useState(null)
  // Check for new follower activity since last profile visit
  const { followingIds: appFollowing, ready: followsReady } = useFollows(user?.id)
  const { toasts: notifToasts, dismiss: dismissNotif } = useRealTimeNotifs(user, appFollowing)
  const onlineUsers = useActiveUsers(user, { poll: !!profile?.is_admin })
  // Warm the co-watch data (friends' rows + pacts) while you're on Home, so the
  // Tonight "Watching with friends" section renders complete + on-top with no
  // load-shift when you get there (2026-07-04). At idle so it never competes with
  // first-paint fetches; the hook fetches anyway if this hasn't finished in time.
  useEffect(() => {
    if (!user?.id || !followsReady) return
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(() => prefetchCoWatch(user.id, appFollowing, followsReady))
  }, [user?.id, followsReady, appFollowing.length])
  // These four badge-count fetches only feed nav dots — nothing on first paint depends on
  // them — so they run at IDLE (mirroring prefetchCoWatch above) instead of racing the
  // watchlist load during the critical boot window. A badge lighting up ~500ms later is
  // invisible; the watchlist arriving sooner is not (perf audit 2026-07-18).
  // Ran ONCE at boot, so a badge could only ever appear on a cold start: anything that
  // arrived while the app was open never lit the Notifications dot, and the app is a PWA people
  // leave open for hours. Verified — a fresh activity showed correctly in the bell drawer
  // (which refetches when opened) while More → Notifications stayed clean (Jason 2026-07-27).
  //
  // Re-checks on the same triggers WhatsNew uses for updates: a 90s poll plus app refocus.
  // Cheap (a HEAD count, no rows) and it only ever sets the flag TRUE — clearing stays
  // with onClearUnseen, so a poll can't wipe a badge the user hasn't looked at.
  const checkUnseenActivity = useCallback(() => {
    if (!user?.id || !appFollowing.length) return
    const lastSeen = safeStorage.get('lastActivitySeen_' + user.id)
    let q = supabase.from('activities').select('id', { count: 'exact', head: true })
      .in('user_id', appFollowing).filter('data->>public', 'eq', 'true')
    if (lastSeen) q = q.gt('created_at', lastSeen)
    q.then(({ count }) => { if (count > 0) setUnseenActivity(true) })
  }, [user?.id, appFollowing])

  useEffect(() => {
    if (!user || !appFollowing.length) return
    // First pass still runs at IDLE so it never races the watchlist during boot.
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(checkUnseenActivity)
    const iv = setInterval(checkUnseenActivity, 90000)
    const onVis = () => { if (document.visibilityState === 'visible') checkUnseenActivity() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis) }
  }, [user?.id, appFollowing.length, checkUnseenActivity])

  // Warm the show page chunk once the first screen is idle. It is the most-opened
  // route after Home (show_view ≈ 200/14d) and, at ~56 KB, the only lazy chunk big
  // enough that a cold open on a phone sits on the spinner long enough to invite a
  // second tap (friction telemetry 2026-10-06: rage taps on Discover cards). Idle, so
  // it never competes with the first paint the cold-boot work in 17.060 protected.
  useEffect(() => {
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1500))
    const h = idle(() => { import('./pages/ShowPage17').catch(() => {}) }, { timeout: 4000 })
    return () => { try { window.cancelIdleCallback?.(h) } catch {} }
  }, [])

  // Count unread notifications: unseen recommendations + pending challenges + inbox
  // notices newer than the inbox's own read marker (notifications_cleared_at). Until
  // 2026-10-11 notices ("X started following you", "Y is back") never lit the nav at
  // all: they are rows with user_id = you and public:false, so neither the
  // recommendations count nor the followed-activity check ever saw them (notification
  // audit). Re-checked every 90s and on foreground, same cadence as friend activity.
  const checkUnseenNotifs = useCallback(() => {
    if (!user?.id) return
    const clearedAt = profile?.notifications_cleared_at || null
    let notices = supabase.from('activities').select('id', { count: 'exact', head: true })
      .eq('user_id', user.id).eq('type', 'notice')
    if (clearedAt) notices = notices.gt('created_at', clearedAt)
    Promise.all([
      supabase.from('recommendations').select('id', { count: 'exact', head: true })
        .eq('to_user', user.id).eq('seen', false),
      supabase.from('challenges').select('id', { count: 'exact', head: true })
        .eq('challenged', user.id).eq('status', 'pending'),
      notices,
    ]).then(([recRes, chalRes, notRes]) => {
      setUnseenNotifs((recRes.count || 0) + (chalRes.count || 0) + (notRes.count || 0))
    }).catch(() => {})
  }, [user?.id, profile?.notifications_cleared_at])
  useEffect(() => {
    if (!user?.id) return
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(checkUnseenNotifs)
    const iv = setInterval(checkUnseenNotifs, 90000)
    const onVis = () => { if (document.visibilityState === 'visible') checkUnseenNotifs() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVis) }
  }, [user?.id, checkUnseenNotifs])

  // ── Hub badges (scoped, personal, self-clearing) ─────────────────────────────
  // New followers since you last opened People, and whether TODAY's Cliffhanger is
  // still unplayed. Both surface on their hub tile + bubble to the More button, and
  // each clears the moment you act (open People / play the puzzle) — no badge sprawl.
  const [newFollowers, setNewFollowers] = useState(0)
  const [cliffUnplayed, setCliffUnplayed] = useState(false)
  useEffect(() => {
    if (!user?.id) return
    const key = 'lastFollowersSeen_' + user.id
    const lastSeen = safeStorage.get(key)
    if (!lastSeen) {
      // First run for this feature: baseline "now" so EXISTING followers don't all
      // count as new on rollout — only follows arriving after this light the badge.
      safeStorage.set(key, new Date().toISOString())
      setNewFollowers(0)
      return
    }
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(() => supabase.from('follows').select('id', { count: 'exact', head: true })
      .eq('following_id', user.id).gt('created_at', lastSeen)
      .then(({ count }) => setNewFollowers(count || 0)).catch(() => {}))
  }, [user?.id])
  useEffect(() => {
    if (!user?.id) return
    const day = dayNumber()  // shared clock — see lib/dailyGame.js
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(() => supabase.from('cliffhanger_results').select('user_id', { count: 'exact', head: true })
      .eq('user_id', user.id).eq('day', day)
      .then(({ count }) => setCliffUnplayed(!count)).catch(() => {}))
    const onScored = () => setCliffUnplayed(false)   // clear the dot the instant you play
    window.addEventListener('nextep:cliffhanger-scored', onScored)
    return () => window.removeEventListener('nextep:cliffhanger-scored', onScored)
  }, [user?.id])
  const [faceoffUnplayed, setFaceoffUnplayed] = useState(false)
  useEffect(() => {
    if (!user?.id) return
    const day = dayNumber()  // shared clock — see lib/dailyGame.js
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(() => supabase.from('faceoff_results').select('user_id', { count: 'exact', head: true })
      .eq('user_id', user.id).eq('day', day)
      .then(({ count }) => setFaceoffUnplayed(!count)).catch(() => {}))
    const onScored = () => setFaceoffUnplayed(false)
    window.addEventListener('nextep:faceoff-scored', onScored)
    return () => window.removeEventListener('nextep:faceoff-scored', onScored)
  }, [user?.id])
  const [verdictUnplayed, setVerdictUnplayed] = useState(false)
  useEffect(() => {
    if (!user?.id) return
    const day = dayNumber()
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 500))
    idle(() => supabase.from('verdict_results').select('user_id', { count: 'exact', head: true })
      .eq('user_id', user.id).eq('day', day)
      .then(({ count }) => setVerdictUnplayed(!count)).catch(() => {}))
    const onScored = () => setVerdictUnplayed(false)
    window.addEventListener('nextep:verdict-scored', onScored)
    return () => window.removeEventListener('nextep:verdict-scored', onScored)
  }, [user?.id])
  // Live wall badge: public activity (not yours) since you last opened the wall.
  const [liveUpdates, setLiveUpdates] = useState(0)
  useEffect(() => {
    if (!user?.id) return
    const lastSeen = liveSince(safeStorage.get('lastLiveSeen_' + user.id), user.created_at)
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 800))
    idle(() => supabase.from('public_activities').select('id', { count: 'exact', head: true })
      .neq('user_id', user.id).gt('created_at', lastSeen)
      .then(({ count }) => setLiveUpdates(Math.min(99, count || 0))).catch(() => {}))
  }, [user?.id])
  const clearLiveUpdates = () => {
    setLiveUpdates(0)
    if (user?.id) safeStorage.set('lastLiveSeen_' + user.id, new Date().toISOString())
  }
  const clearFollowers = () => {
    setNewFollowers(0)
    if (user?.id) safeStorage.set('lastFollowersSeen_' + user.id, new Date().toISOString())
  }

  // Repair push subscriptions stranded by the broken-key window (2026-06-11):
  // anyone who already granted permission silently re-subscribes with the correct
  // VAPID key on load, so notifications start working again with no user action.
  useEffect(() => {
    if (!user?.id) return
    import('./lib/push').then(m => m.ensureFreshSubscription(user.id)).catch(() => {})
  }, [user?.id])

  const raceShowIds = useActiveRaces(user?.id)
  const wl = useWatchlist(user, { shareActivity, onBinge: (show) => { setBingeShow(show); setTimeout(() => setBingeShow(null), 4000) },
    // A signed-out Add (the callback carries the show) takes the guest door: an
    // anonymous session + the row, exactly like /start, and the app unlocks in place.
    // Both new visitors on 2026-09-26 hit the sign-in wall here and left with 0 shows.
    // Everything else that needs an account (marking, rating) still gets the modal.
    onAuthRequired: (show) => {
      if (show?.id) { guestAddShow(show, { supabase }).then(r => { if (r.ok) pendingGuestAdd.current = show; else if (r.reason === 'write-failed') reportSaveError(r, 'add to list'); else setShowAuthModal(true) }); return }
      setShowAuthModal(true)
    },
  })
  // The guest door can't fire nextep:show_added itself: at that moment useAuth hasn't
  // promoted the session and wl has no rows, so the "where are you with it?" sheet
  // (ShowAddedSheet) would open on a list that can't be written to. Fire it the moment
  // the new guest's list actually contains the show — position-setting is the step
  // that predicts a first mark, and the guest door must not skip it.
  const pendingGuestAdd = useRef(null)
  useEffect(() => {
    const s = pendingGuestAdd.current
    if (!s || !user || !wl?.ready || !wl.items?.some(i => i.id === s.id)) return
    pendingGuestAdd.current = null
    window.dispatchEvent(new CustomEvent('nextep:show_added', { detail: { ...s, isFirstShow: true, guest: true } }))
  }, [user, wl?.ready, wl?.items])

  // Now Playing skin: the queue's top show (first one marked "watching", else the newest)
  // is the poster the app takes its colour from. lib/nowPlaying.js no-ops unless the skin
  // is active, so this costs nothing on every other theme.
  useEffect(() => {
    if (!wl?.ready) return
    const items = wl.items || []
    const pick = items.find(s => wl.statuses?.[s.id] === 'watching') || items[0] || null
    setNowPlayingBase(pick)
  }, [wl?.ready, wl?.items, wl?.statuses])

  // Event-driven layers (cinematics, toasts, pulse, what's new) mount after the first
  // deck paint, not with it: they only react to user actions, so nothing is lost.
  const [booted, setBooted] = useState(false)
  useEffect(() => {
    if (booted) return
    const go = () => setBooted(true)
    if (wl?.ready) { const id = setTimeout(go, 0); return () => clearTimeout(id) }
    const ric = window.requestIdleCallback
    const id = ric ? ric(go, { timeout: 2000 }) : setTimeout(go, 2000)
    return () => { if (ric) window.cancelIdleCallback(id); else clearTimeout(id) }
  }, [wl?.ready, booted])

  if (loading) return <BootSplash />

  return (
    <ToastProvider>
    <>
      <>
        <OfflineBanner />
        <ActivityToastStack toasts={notifToasts} onDismiss={dismissNotif}/>
        <EdgeSwipeBack />
        {booted && <ErrorBoundary label="notif layer" fallback={null}>
          <Suspense fallback={null}><NotifLayer user={user} profile={profile} wl={wl} /></Suspense>
        </ErrorBoundary>}
        {booted && user && <ErrorBoundary label="episode pulse" fallback={null}><Suspense fallback={null}><EpisodePulseHost /></Suspense></ErrorBoundary>}
        {user && !user.is_anonymous && <ErrorBoundary label="passkey nudge" fallback={null}><Suspense fallback={null}><PasskeyNudge user={user} /></Suspense></ErrorBoundary>}
        {booted && user && !user.is_anonymous && showWhatsNew && <ErrorBoundary label="what's new" fallback={null}><Suspense fallback={null}><WhatsNew oneTapUpdate onDismiss={() => setShowWhatsNew(false)} /></Suspense></ErrorBoundary>}
        {showWelcome && user && (
          <ErrorBoundary label="welcome walkthrough" fallback={null}>
          <Suspense fallback={null}><WelcomeWalkthrough user={user} profile={profile} myServices={myServices} featureFlags={featureFlags} wl={wl} onComplete={handleWelcomeDone} /></Suspense>
          </ErrorBoundary>
        )}
        <AppRoutes
          user={user} profile={profile} wl={wl} booted={booted}
          signOut={signOut} refreshProfile={refreshProfile}
          showAuthModal={showAuthModal} setShowAuthModal={setShowAuthModal}
          myServices={myServices}
          featureFlags={featureFlags}
          unseenActivity={unseenActivity} setUnseenActivity={setUnseenActivity}
          unseenNotifs={unseenNotifs} onClearNotifs={() => setUnseenNotifs(0)}
          onClearUnseen={() => { setUnseenActivity(false); if (user?.id) safeStorage.set('lastActivitySeen_' + user.id, new Date().toISOString()) }}
          newFollowers={newFollowers} cliffUnplayed={cliffUnplayed} faceoffUnplayed={faceoffUnplayed} verdictUnplayed={verdictUnplayed} liveUpdates={liveUpdates} onOpenLive={clearLiveUpdates} onClearFollowers={clearFollowers}
          bingeShow={bingeShow} setBingeShow={setBingeShow}
          onlineUsers={onlineUsers}
          raceShowIds={raceShowIds}
        />
      </>
    </>
    </ToastProvider>
  )
}
